import React, { useState, useEffect, useMemo } from 'react';
import { fetchAccessPointPickups } from '../services/api';
import { toTitleCase } from '../utils/format';

// UPS Access Point pickup queue (Greg, 2026-10-13). Like the fulfillment queue: what is
// still waiting at a store stays on top, longest wait first, and a picked-up order falls
// to the bottom. The colour is how long it has sat at the store, so CS knows when to call.
// The query is `access_point_pickups` in netlify/functions/neon.js; it reads UPS's own
// scans, so only a true Access Point hold ever shows "waiting" or "picked up".

const DAY_MS = 86400000;
const CALL_AT = 5;   // days at the store before CS should call (green 0-2, yellow 3-4, red 5+)
const DASH = '\u2014';

const day = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const when = (v) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const daysWaiting = (r) => Math.max(0, Math.floor((Date.now() - new Date(r.ready_at).getTime()) / DAY_MS));
const waitColor = (d) => (d >= CALL_AT ? 'var(--red)' : d >= 3 ? 'var(--yellow)' : 'var(--green)');
const upsLink = (t) => 'https://www.ups.com/track?tracknum=' + encodeURIComponent(t);
const telLink = (p) => 'tel:' + String(p).replace(/[^\d+]/g, '');

const RANK = { waiting: 0, in_transit: 1, not_shipped: 2, picked_up: 3, returned: 3, store_delivery: 3 };
const STATE_LABEL = {
  in_transit: 'On the way to the store',
  not_shipped: 'Not shipped yet',
  picked_up: 'Picked up',
  returned: 'Returned by UPS',
  store_delivery: 'Left at the store address',
};
const finishedAt = (r) => new Date(r.picked_up_at || r.delivered_at || r.returned_at || 0).getTime();

function Badge({ color, children }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      color: '#fff', background: color, whiteSpace: 'nowrap' }}>{children}</span>
  );
}

function Status({ r }) {
  const sub = { color: 'var(--gray-500)', fontSize: 11 };
  if (r.state === 'waiting') {
    const d = daysWaiting(r);
    return (
      <>
        <Badge color={waitColor(d)}>{d === 0 ? 'At the store today' : d + (d === 1 ? ' day' : ' days') + ' at the store'}</Badge>
        {d >= CALL_AT && <div style={{ color: 'var(--red)', fontWeight: 600, marginTop: 4 }}>Call the customer</div>}
        <div style={{ ...sub, marginTop: 3 }}>Ready for pickup {when(r.ready_at)}</div>
      </>
    );
  }
  const done = RANK[r.state] === 3;
  return (
    <>
      <div style={{ fontWeight: 600, color: done ? 'var(--gray-500)' : 'var(--navy)' }}>{STATE_LABEL[r.state]}</div>
      {r.state === 'picked_up' && r.picked_up_at && <div style={sub}>{when(r.picked_up_at)}</div>}
      {r.state === 'store_delivery' && (
        <div style={sub}>
          {when(r.delivered_at)}. Shipped as a normal delivery, not an Access Point hold, so UPS does not report the pickup.
        </div>
      )}
      {r.state === 'returned' && <div style={sub}>{when(r.returned_at)}</div>}
      {r.state === 'in_transit' && r.last_status && <div style={sub}>{r.last_status}, {when(r.last_at)}</div>}
      {r.state === 'in_transit' && !r.is_hold && (
        <div style={{ color: 'var(--orange)', fontSize: 11 }}>Not an Access Point hold. UPS will not report the pickup.</div>
      )}
      {r.state === 'not_shipped' && r.ship_date && <div style={sub}>Ship date {day(r.ship_date)}</div>}
    </>
  );
}

function Email({ r }) {
  if (!r.is_hold) return <span style={{ color: 'var(--gray-400)' }}>{DASH}</span>;
  if (r.email_sent && r.email_mode === 'customers') return <span>Sent {when(r.email_sent_at)}</span>;
  if (r.email_sent) return <span style={{ color: 'var(--gray-500)' }}>Test copy to Greg {when(r.email_sent_at)}, not the customer</span>;
  if (r.state === 'waiting') return <span style={{ color: 'var(--red)' }}>Not sent</span>;
  return <span style={{ color: 'var(--gray-400)' }}>Sends when it reaches the store</span>;
}

export default function AccessPointPickups({ navigateTo }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [showDone, setShowDone] = useState(false);
  useEffect(() => { fetchAccessPointPickups().then(setRows).catch(e => setErr(e.message)); }, []);

  const sorted = useMemo(() => (rows || []).slice().sort((a, b) => {
    const ra = RANK[a.state] ?? 9; const rb = RANK[b.state] ?? 9;
    if (ra !== rb) return ra - rb;
    if (a.state === 'waiting') return new Date(a.ready_at) - new Date(b.ready_at);
    if (ra === 3) return finishedAt(b) - finishedAt(a);
    return String(a.ship_date || '').localeCompare(String(b.ship_date || ''));
  }), [rows]);

  const waiting = sorted.filter(r => r.state === 'waiting');
  const needCall = waiting.filter(r => daysWaiting(r) >= CALL_AT);
  const done = sorted.filter(r => RANK[r.state] === 3);
  const list = showDone ? sorted : sorted.filter(r => RANK[r.state] !== 3);

  const tile = (label, value, sub, color) => (
    <div style={{ flex: '1 1 150px', background: 'white', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--navy)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
  const th = { textAlign: 'left', padding: '8px 10px', color: 'var(--gray-500)', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', verticalAlign: 'top' };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
        Packages waiting at a UPS Access Point for the customer to collect, longest wait first.
        Green is 0-2 days at the store, yellow 3-4, red {CALL_AT}+ (call the customer). Picked-up orders drop to the bottom.
      </div>

      {err && <div className="card" style={{ padding: 16, color: 'var(--red)', fontSize: 13 }}>Error: {err}</div>}
      {!rows && !err && <div className="card" style={{ padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}

      {rows && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {tile('Waiting at a store', waiting.length)}
            {tile('Need a call', needCall.length, CALL_AT + '+ days at the store', needCall.length ? 'var(--red)' : null)}
            {tile('On the way', sorted.filter(r => r.state === 'in_transit').length)}
            {tile('Not shipped yet', sorted.filter(r => r.state === 'not_shipped').length)}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, borderBottom: '1px solid var(--gray-200)' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} />
                Show finished orders ({done.length})
              </label>
              <span style={{ marginLeft: 'auto', color: 'var(--gray-500)' }}>{list.length} shown</span>
            </div>
            {list.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Nothing waiting for pickup.</div>}
            {list.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={th}>Order</th>
                      <th style={th}>Access Point</th>
                      <th style={{ ...th, minWidth: 220 }}>Status</th>
                      <th style={th}>Pickup email</th>
                      <th style={th}>Tracking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(r => (
                      <tr key={r.bc_order_id} style={{ borderBottom: '1px solid var(--gray-100)', opacity: RANK[r.state] === 3 ? 0.7 : 1 }}>
                        <td style={td}>
                          <span style={{ color: 'var(--orange)', fontWeight: 600, cursor: 'pointer' }}
                            onClick={() => navigateTo?.('order-details', r.bc_order_id)}>{r.bc_order_id}</span>
                          <div style={{ color: 'var(--navy)' }}>{toTitleCase(r.customer || '')}</div>
                          {r.phone && <div><a href={telLink(r.phone)} style={{ color: 'var(--navy)' }}>{r.phone}</a></div>}
                          {r.email && <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{r.email}</div>}
                          {r.bc_error && <div style={{ color: 'var(--orange)', fontSize: 11 }}>Could not read the phone. Open the order.</div>}
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.store || 'Store name not on the order'}</div>
                          <div style={{ color: 'var(--gray-600)' }}>{r.store_address}</div>
                        </td>
                        <td style={td}><Status r={r} /></td>
                        <td style={td}><Email r={r} /></td>
                        <td style={td}>
                          {r.tracking
                            ? <a href={upsLink(r.tracking)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)', fontSize: 11 }}>{r.tracking}</a>
                            : <span style={{ color: 'var(--gray-400)' }}>{DASH}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}